/**
 * The selected day's covers breakdown and running order (POS2-1259 CP3).
 *
 * The write affordances are optional props: a viewer without `occupancy:update` gets the same
 * panel with no Edit, no Delete and no Add — the affordance is absent rather than present and
 * refused.
 */
import { Button } from "@/components/ui/button";
import type { DayCovers, OccupancyEvent, ServiceStyle, WasteBasis } from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { formatExpectedWaste, orderedEvents, shortTime, TYPE_COLOURS } from "@/lib/occupancy";
import { cn } from "@/lib/utils";

/** Literal keys — a key built at runtime is invisible to `check:i18n`. */
const SERVICE_STYLE_KEYS: Record<ServiceStyle, string> = {
  plated: "occupancy.serviceStyle.plated",
  buffet: "occupancy.serviceStyle.buffet",
  family_style: "occupancy.serviceStyle.family_style",
  canapes: "occupancy.serviceStyle.canapes",
  day_delegate: "occupancy.serviceStyle.day_delegate",
};

export function DayPanel({
  day,
  capacity,
  basis,
  onEditBase,
  onEditEvent,
  onDeleteEvent,
  onAddEvent,
}: {
  day: DayCovers;
  capacity: number | null;
  basis?: WasteBasis;
  onEditBase?: () => void;
  onEditEvent?: (event: OccupancyEvent) => void;
  onDeleteEvent?: (event: OccupancyEvent) => void;
  onAddEvent?: () => void;
}) {
  const { t } = useTranslation();
  const events = orderedEvents(day.events);
  const over = capacity !== null && day.total !== null && day.total > capacity;

  return (
    <aside className="flex flex-col gap-4 rounded-xl border border-border bg-background p-4">
      <div>
        <h3 className="text-sm font-bold tracking-tight">{day.date}</h3>
        <p className="text-[13px] text-muted-foreground">
          {events.length === 0
            ? t("occupancy.panel.noEvents")
            : t("occupancy.panel.events", { count: events.length })}
        </p>
      </div>

      <dl className="flex flex-col gap-1.5 text-[13px]">
        <div className="flex items-center justify-between gap-3">
          <dt className="flex items-center gap-1.5 text-muted-foreground">
            {t("occupancy.panel.baseCovers")}
            {day.base_source === "override" && (
              <span className="rounded border border-border px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t("occupancy.panel.edited")}
              </span>
            )}
            {onEditBase && (
              <Button variant="ghost" size="sm" className="h-5 px-1 text-xs" onClick={onEditBase}>
                {t("occupancy.action.edit")}
              </Button>
            )}
          </dt>
          <dd className="tabular-nums">
            {day.base ?? <span className="text-muted-foreground">—</span>}
          </dd>
        </div>

        {events.map((event) => (
          <div key={event.id} className="flex items-center justify-between gap-3">
            <dt className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
              <span
                className="size-2 shrink-0 rounded-[2px]"
                style={{ background: TYPE_COLOURS[event.event_type] }}
              />
              <span className="truncate">{event.name}</span>
            </dt>
            <dd className="tabular-nums">{event.covers}</dd>
          </div>
        ))}

        <div className="mt-1 flex items-center justify-between gap-3 border-t border-border pt-2 font-semibold">
          <dt>{t("occupancy.panel.total")}</dt>
          <dd className={cn("tabular-nums", over && "text-destructive")}>
            {day.total ?? <span className="font-normal text-muted-foreground">—</span>}
          </dd>
        </div>
        {over && day.total !== null && capacity !== null && (
          <p className="text-right text-xs text-destructive">
            {t("occupancy.panel.overCapacity", { count: day.total - capacity })}
          </p>
        )}
        {day.base === null && (
          <p className="text-xs text-muted-foreground">{t("occupancy.panel.noBase")}</p>
        )}
      </dl>

      {events.length > 0 && (
        <div className="flex flex-col gap-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("occupancy.panel.runningOrder")}
          </h4>
          {events.map((event) => {
            const start = shortTime(event.start_time);
            const end = shortTime(event.end_time);
            return (
              <div key={event.id} className="rounded-lg border border-border p-2.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-[13px] font-semibold">{event.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {start && end ? `${start}–${end}` : (start ?? "")}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                  <span className="tabular-nums">{event.covers}</span>
                  {event.room && <span className="truncate">{event.room}</span>}
                  {event.service_style && <span>{t(SERVICE_STYLE_KEYS[event.service_style])}</span>}
                </div>
                {basis && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("occupancy.drawer.expectedWaste")}:{" "}
                    {formatExpectedWaste(event.covers, basis).headline}
                  </p>
                )}
                {event.notes && <p className="mt-1 text-xs text-muted-foreground">{event.notes}</p>}
                {(onEditEvent || onDeleteEvent) && (
                  <div className="mt-1.5 flex gap-1">
                    {onEditEvent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-xs"
                        onClick={() => onEditEvent(event)}
                      >
                        {t("occupancy.action.edit")}
                      </Button>
                    )}
                    {onDeleteEvent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-xs text-destructive hover:text-destructive"
                        onClick={() => onDeleteEvent(event)}
                      >
                        {t("occupancy.action.delete")}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {onAddEvent && (
        <Button variant="outline" size="sm" className="self-start" onClick={onAddEvent}>
          {t("occupancy.action.addEvent")}
        </Button>
      )}
    </aside>
  );
}
